import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import SwitchTabs from '../../components/Dashboard/SwitchTabs';
import FailedTemplates from './FailedTemplates';
import TopTenTemplates from './TopTenTemplates';
import RadialChart from '../../components/Dashboard/RadialBarChart';
import LineChart from '../../components/Dashboard/LineChart';
import {
  getDetails,
  getFailureCountDetails,
  getTopTenFailureTemplateDetails,
  getDocumentDashboardChartData,
} from '../../utils/services/dashboardAPI';
import { getAllTeams } from '../../utils/services/teamsAPI';
import { getLastTwelveMonths } from '../../utils/javascript';
import { DEFAULT_DATE_TIME_FORMAT, READABLE_DATE_TIME_FORMAT } from '../../utils/helper';
import {
  Dashboard,
  Sidebar,
  Loader,
  Title,
  Filters,
  CardsContainer,
  Cards,
  Subtitle,
  FailureReasons,
  LinkText,
  TemplatesList,
  ChartsSection,
  CloseIcon,
  TemplateLink,
  ModalData,
  ModalHeading
} from './newDashboardStyles.js'
import { Select, DatePicker, Icon, Spin, Modal, Tooltip } from 'antd';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { DOCUMENT_DASHBOARD, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE, FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL } from './TranslationValues';
import { DashboardErrorReport } from './DashboardErrorReport.js';
import { getJobRecordDataset } from '../../utils/services/statusMonitoringAPI.js';
import { getAllLanguage } from '../../utils/services/languageApi.js';

const { RangePicker } = DatePicker;
const { Option } = Select;

const SHOW_SYSTEM_SPEED_AND_USER_EXPERIENCE_METRICS = true;


const NAMESPACES = ['DOCUMENT_DASHBOARD', 'FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE', 'FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL']
const FAILED_TRANSACTIONS_TABLE = 'FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE'
const FAILURE_TEMPLATES_MODAL = 'FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL'
const DocumentDashboard = props => {
  const { t } = props
  const today = new Date();
  const previous = new Date(today.getTime());
  const previousDate = previous.setDate(today.getDate() - 1);
  const startDate = moment(previousDate).format(DEFAULT_DATE_TIME_FORMAT);
  const endDate = moment(today).format(DEFAULT_DATE_TIME_FORMAT);
  const [activeTab, setActiveTab] = useState(0);
  const [templatesView, setTemplatesView] = useState(false);
  const [viewType, setViewType] = useState(null);
  const [date, setDate] = useState({
    startDate: startDate,
    endDate: endDate,
  });
  const [documentCount, setDocumentCount] = useState(null);
  const [failureCount, setFailureCount] = useState(null);
  const [failurePercentage, setFailurePercentage] = useState(0);
  const [topTenFailureTemplates, setTopTenFailureTemplates] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [chartData, setChartData] = useState({
    averageGenerationTime: [],
    averageSuccess: [],
    averageFailure: [],
  });
  const [sideBarLoader, setSideBarLoader] = useState(false);
  const [chartSectionLoader, setChartSectionLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ failureDetails: '', payload: '', runtimeError: null });
  const [languages, setLanguages] = useState([]);

  const initialRenderRef = useRef(true);

  const showModal = data => {
    setIsModalOpen(true);
    setModalData(data);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setModalData({ failureDetails: '', payload: '', runtimeError: null });
  };

  const size = {
    sideBarWidth: '30%',
    chartSectionWidth: '70%',
  };

  const handleTemplateIdClick = useCallback(
    templateId => {
      window.open(`/design/${templateId}`, "_blank");
    },
    [],
  );

  const getPayload = async transactionId => {
    const res = await getJobRecordDataset(transactionId, 'print');
    if (res && res.statusCode === 5008 && res.data) {
      if (res.data.payload) {
        const dataSet = res.data.payload;
        return dataSet;
      }
    }
    return "";
  };

  const getLanguageNameByCode = (code) => {
    const language = languages.find(lang => lang.code === code);
    return language ? language.name : null; 
  };

  const columns = useMemo(
    () => [
      {
        title: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.REQUESTED_TIME_COLUMN_TITLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.REQUESTED_TIME_COLUMN_TITLE.value)}`,
        sorter: (a, b) => a.age - b.age,
        dataIndex: 'requestedTime',
        key: 'requestedTime',
        render: date => moment(date).format(READABLE_DATE_TIME_FORMAT),
      },
      {
        title: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.PROCESS_INITIATED_FROM_COLUMN_TITLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.PROCESS_INITIATED_FROM_COLUMN_TITLE.value)}`,
        dataIndex: 'processInitiatedBy',
        key: 'processInitiatedBy',
        render: data => {
          if (data === 'Correspondence Generation') {
            return 'Third Party';
          }
          return 'Bulk';
        },
      },
      {
        title: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.TEMPLATE_COLUMN_TITLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.TEMPLATE_COLUMN_TITLE.value)}`,
        dataIndex: 'templateName',
        key: 'templateName',
        render: (name, row) => {
          if (row.templateId) {
            return <TemplateLink onClick={() => handleTemplateIdClick(row.templateId)}>{name}</TemplateLink>
          } else {
            return name;
          }
        },
      },
      {
        title: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_REASONS_COLUMN_TITLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_REASONS_COLUMN_TITLE.value)}`,
        dataIndex: 'failureReason',
        key: 'failureReason',
        filters: [
          { text: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_REASONS_INVALID_DATA_DROPDOWN_OPTION_LABLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_REASONS_INVALID_DATA_DROPDOWN_OPTION_LABLE.value)}`, value: 'integration' },
          { text: 'Doc Studio', value: 'docstudio' },
          { text: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_REASONS_UNKNOWN_DROPDOWN_OPTION_LABLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_REASONS_UNKNOWN_DROPDOWN_OPTION_LABLE.value)}`, value: 'unknown' },
        ],
        filterMultiple: false,
      },
      {
        title: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_DETAILS_COLUMN_TITLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_DETAILS_COLUMN_TITLE.value)}`,
        dataIndex: 'failureDesc',
        key: 'failureDesc',
        render: (data, { payLoad, runtimeError }) => (
          <>
            {data?.length > 30 ? (
              <span>
                <TemplateLink
                  onClick={async () => {
                    const payloadData = await getPayload(payLoad);
                    showModal({
                      failureDetails: data,
                      payload: payloadData,
                      runtimeError: runtimeError,
                    });
                  }}
                >
                  {t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.VIEW_FAILURE_DETAILS_ACTION.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.VIEW_FAILURE_DETAILS_ACTION.value)}
                </TemplateLink>
              </span>
            ) : (
              data
            )}
          </>
        ),
      },
      {
        title: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_TIME_COLUMN_TITLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILURE_TIME_COLUMN_TITLE.value)}`,
        dataIndex: 'failureTime',
        key: 'failureTime',
        render: date => moment(date).format(READABLE_DATE_TIME_FORMAT),
      },
      {
        title: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.TEAM_NAME_COLUMN_LABLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.TEAM_NAME_COLUMN_LABLE.value)}`,
        dataIndex: 'teamName',
        key: 'teamName',
      },
      {
        title: `${t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.LANGUAGE_COLUMN_TITLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.LANGUAGE_COLUMN_TITLE.value)}`,
        dataIndex: 'language',
        key: 'language',
        render: (data) => {
          return getLanguageNameByCode(data);
        }
      },
    ],
    [handleTemplateIdClick, t],
  );

  function disabledDate(current) {
    return current > moment() || current < moment().subtract(7, 'day');
  }

  const onDateChange = dates => {
    let startDate = moment(dates[0]).format(DEFAULT_DATE_TIME_FORMAT);
    let endDate = moment(dates[1]).format(DEFAULT_DATE_TIME_FORMAT);
    setDate({
      startDate: startDate,
      endDate: endDate,
    });
  };

  const dashboardAPI = useCallback(
    async (payload) => {
      const payloadData = { env: activeTab, payload: payload };
      try {
        setSideBarLoader(true);
        const APIs = [getDetails(payloadData), getFailureCountDetails(payloadData)];
        if(initialRenderRef.current) {
          setChartSectionLoader(true);
          APIs.push(getAllLanguage(), getAllTeams(), getDocumentDashboardChartData());
          initialRenderRef.current = false;
        }
        if(viewType === 1) {
          setChartSectionLoader(true);
          APIs.push(getTopTenFailureTemplateDetails(payloadData));
        }
        const apiResponses = await Promise.allSettled(APIs);
        apiResponses.forEach((res) => {
          switch(res?.value?.statusCode) {
            case 109: {
                setLanguages(res?.value?.data);
                break;
              }
            case 1702: {
                setDocumentCount(res?.value?.data.documentCounts);
                let total = res?.value?.data.documentCounts.failed + res?.value?.data.documentCounts.generated;
                let percentage = Math.floor((res?.value?.data.documentCounts.failed / total) * 100);
                setFailurePercentage(isNaN(percentage) ? 0 : percentage);
                break;
              }
            case 1706: {
                setFailureCount(res?.value?.data.failureReasonStats);
                break;
              }
            case 1408: {
                setTeams(res?.value?.data);
                break;
              }
            case 1538: {
                chartDataAPI(res?.value);
                break;
              }
            case 1704: {
                setTopTenFailureTemplates(res?.value?.data.topTenFailedTemplates);
                break;
              }
            default: break;
          }
        });
      } catch (e) {
        setDocumentCount({ queue: 0, generated: 0, failed: 0 });
        setFailureCount({ integration: 0, docstudio: 0, unknown: 0 });
        console.error(e);
      } finally {
        setSideBarLoader(false);
        setChartSectionLoader(false);
      }
    },
    [activeTab, viewType],
  );

  const topTenFailureAPI = async () => {
    let payload = {
      startDate: date.startDate,
      endDate: date.endDate,
    };
    if (selectedTeam) {
      payload.teamId = selectedTeam;
    }
    try {
      setChartSectionLoader(true);
      const res = await getTopTenFailureTemplateDetails({ env: activeTab, payload: payload });
      setTopTenFailureTemplates(res.data.topTenFailedTemplates);
      setChartSectionLoader(false);
    } catch (e) {
      console.log(e);
      setChartSectionLoader(false);
    }
  };

  const chartDataAPI = useCallback(res => {

      const averageGenerationTimeArray = Array(12).fill(0);
      const averageSuccessArray = Array(12).fill(0);
      const averageFailureArray = Array(12).fill(0);

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      for (const data of res.data) {
        const { month, year, averageGenerationTime, averageSuccess, averageFailure } = data;
        const monthsAgo = (currentYear - year) * 12 + (currentMonth - month);

        if (monthsAgo >= 0 && monthsAgo < 12) {
          averageGenerationTimeArray[11 - monthsAgo] = averageGenerationTime;
          averageSuccessArray[11 - monthsAgo] = averageSuccess;
          averageFailureArray[11 - monthsAgo] = averageFailure; // Reverse the order
        }
      }
      setChartData({
        averageGenerationTime: averageGenerationTimeArray,
        averageSuccess: averageSuccessArray,
        averageFailure: averageFailureArray,
      });
  }, []);


  useEffect(() => {
    let payload = {
      startDate: date.startDate,
      endDate: date.endDate,
    };
    if (selectedTeam) {
      payload.teamId = selectedTeam;
    }
    dashboardAPI(payload);
  }, [activeTab, date.endDate, date.startDate, selectedTeam]);

  const handleChange = value => {
    setSelectedTeam(value);
  };

  const getFailurePercentages = useCallback((reason) => {
    if (failureCount) {
      const totalFailedDocuments =
        failureCount.docstudio + failureCount.integration + failureCount.unknown;
      const percentageVal = (failureCount[reason] / totalFailedDocuments) * 100
      if (totalFailedDocuments === 0) {
        return "0%"
      }
      if (Number.isInteger(percentageVal)) {
        return `${percentageVal}%`
      }
      return `${percentageVal.toFixed(1)}%`
    }
    return "0%"
  }, [failureCount]);

  const isPayloadValid = !!modalData?.payload?.replaceAll('{}', '');

  return (
    <Dashboard>
      <Sidebar width={size.sideBarWidth}>
        {sideBarLoader && (
          <Loader>
            <Spin size={'large'} />
          </Loader>
        )}
        <Title height={'10%'}>{t(DOCUMENT_DASHBOARD.DOCUMENT_DASHBOARD_TITLE.key, DOCUMENT_DASHBOARD.DOCUMENT_DASHBOARD_TITLE.value)}</Title>
        <Filters height={'15%'}>
          <SwitchTabs
            items={[
              `${t(
                DOCUMENT_DASHBOARD.PUBLISHED_TAB_TITLE.key,
                DOCUMENT_DASHBOARD.PUBLISHED_TAB_TITLE.value,
              )}`,
              `${t(
                DOCUMENT_DASHBOARD.TESTING_TAB_TITLE.key,
                DOCUMENT_DASHBOARD.TESTING_TAB_TITLE.value,
              )}`,
              `${t(
                DOCUMENT_DASHBOARD.DRAFT_TAB_TITLE.key,
                DOCUMENT_DASHBOARD.DRAFT_TAB_TITLE.value,
              )}`,
            ]}
            width={'360px'}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Select
            showSearch
            style={{ minWidth: '28%' }}
            optionFilterProp="children"
            defaultValue={'All'}
            value={selectedTeam}
            onChange={handleChange}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option value="" selected>
              {t(DOCUMENT_DASHBOARD.ALL_TEMPLATE_CATEGORY_DROPDOWN_OPTION.key, DOCUMENT_DASHBOARD.ALL_TEMPLATE_CATEGORY_DROPDOWN_OPTION.value)}
            </Option>
            {teams.map(team => (
              <Option key={team.id} value={team.id}>
                <Tooltip key={team.id} placement="topLeft" title={team.name}>{team.name}</Tooltip>
              </Option>
            ))}
          </Select>
          <RangePicker
            disabledDate={disabledDate}
            inputReadOnly={true}
            allowClear={false}
            showTime={{
              hideDisabledOptions: true,
              defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('11:59:59', 'HH:mm:ss')],
            }}
            defaultValue={[moment().subtract(1, 'day'), moment()]}
            format="YYYY-MM-DD HH:mm:ss"
            onOk={e => onDateChange(e)}
          />

        </Filters>
        <CardsContainer height={'40%'}>
          <Cards
            id={1}
            templatesCount={true}
            animation={'popUp'}
            animationDelay={'400ms'}
            countFontSize={'1.5rem'}
            height={'38%'}
            width={'68%'}
            textColor={'#000'}
            bgColor={'#F9DF25'}
          >
            <h2 className="title">{t(DOCUMENT_DASHBOARD.TOTAL_DOCUMENT_GENERATED_STATS_LABEL.key, DOCUMENT_DASHBOARD.TOTAL_DOCUMENT_GENERATED_STATS_LABEL.value)}</h2>
            <h1 className="count">{documentCount?.generated || 0}</h1>
          </Cards>
          <Cards
            id={2}
            templatesCount={true}
            animation={'popUp'}
            animationDelay={'600ms'}
            countFontSize={'1.3rem'}
            height={'40%'}
            width={'35%'}
            textColor={'#FFF'}
            bgColor={'#FF4B24'}
          >
            <h2 className="title">{t(DOCUMENT_DASHBOARD.INQUEUE_STATS_LABEL.key, DOCUMENT_DASHBOARD.INQUEUE_STATS_LABEL.value)}</h2>
            <h1 className="count">{documentCount?.queue || 0}</h1>
          </Cards>
          <Cards
            id={2}
            templatesCount={true}
            animation={'popUp'}
            animationDelay={'600ms'}
            countFontSize={'1.3rem'}
            height={'40%'}
            width={'55%'}
            textColor={'#FFF'}
            bgColor={'#9C179E'}
          >
            <h2 className="title">{t(DOCUMENT_DASHBOARD.FAILURE_STATS_LABEL.key, DOCUMENT_DASHBOARD.FAILURE_STATS_LABEL.value)}</h2>
            <h1 className="count">
              {documentCount?.failed || 0}
              <button
                className="linkTag linkBtn"
                style={{ display: 'block' }}
                onClick={() => {
                  setViewType(0);
                  setTemplatesView(true);
                }}
              >
                {t(DOCUMENT_DASHBOARD.VIEW_FAILED_TRANSACTIONS_ACTION.key, DOCUMENT_DASHBOARD.VIEW_FAILED_TRANSACTIONS_ACTION.value)}
              </button>
            </h1>
            <RadialChart value={failurePercentage} color={['#6EE6E4']} textColor={'#FFF'} />
          </Cards>
        </CardsContainer>
        <Subtitle height={'7%'}>{t(DOCUMENT_DASHBOARD.INSIGHTS_FAILURE_REASON_SECTION_TITLE.key, DOCUMENT_DASHBOARD.INSIGHTS_FAILURE_REASON_SECTION_TITLE.value)}</Subtitle>
        <FailureReasons height={'23%'}>
          <Cards
            animation={'fadeIn'}
            animationDelay={'400ms'}
            height={'120px'}
            width={'98px'}
            bgColor={'#FFF'}
          >
            <div className="icon"></div>
            <h2 className="text">{t(DOCUMENT_DASHBOARD.FAILURE_REASONS_INVALID_DATA_CARD_TITLE.key, DOCUMENT_DASHBOARD.FAILURE_REASONS_INVALID_DATA_CARD_TITLE.value)}</h2>
            <h1 className="percentage">{getFailurePercentages('integration')}</h1>
          </Cards>
          <Cards
            animation={'fadeIn'}
            animationDelay={'600ms'}
            height={'120px'}
            width={'98px'}
            bgColor={'#FFF'}
          >
            <div className="icon"></div>
            <h2 className="text">Doc Studio</h2>
            <h1 className="percentage">{getFailurePercentages('docstudio')}</h1>
          </Cards>
          <Cards
            animation={'fadeIn'}
            animationDelay={'800ms'}
            height={'120px'}
            width={'98px'}
            bgColor={'#FFF'}
          >
            <div className="icon"></div>
            <h2 className="text">{t(DOCUMENT_DASHBOARD.FAILURE_REASONS_UNKNOWN_CARD_TITLE.key, DOCUMENT_DASHBOARD.FAILURE_REASONS_UNKNOWN_CARD_TITLE.value)}</h2>
            <h1 className="percentage">{getFailurePercentages('unknown')}</h1>
          </Cards>
        </FailureReasons>
        <LinkText height={'5%'}>
          <button
            className="linkBtn"
            onClick={() => {
              setViewType(1);
              setTemplatesView(true);
              topTenFailureAPI();
            }}
          >
            {t(DOCUMENT_DASHBOARD.VIEW_TEMPLATES_PRONE_TO_FAILURE_ACTION.key, DOCUMENT_DASHBOARD.VIEW_TEMPLATES_PRONE_TO_FAILURE_ACTION.value)}
          </button>
        </LinkText>
      </Sidebar>
      <ChartsSection width={size.chartSectionWidth}>
        {chartSectionLoader && (
          <Loader>
            <Spin size={'large'} />
          </Loader>
        )}
        <LineChart
          title={t(DOCUMENT_DASHBOARD.USAGE_TREND_CHART_TITLE.key, DOCUMENT_DASHBOARD.USAGE_TREND_CHART_TITLE.value)}
          description={t(DOCUMENT_DASHBOARD.USAGE_TREND_CHART_DESCRIPTION.key, DOCUMENT_DASHBOARD.USAGE_TREND_CHART_DESCRIPTION.value)}
          height={250}
          width={370}
          colors={['#F55430']}
          categories={getLastTwelveMonths()}
          series={[{ name: '', data: chartData.averageGenerationTime }]}
        />
        {SHOW_SYSTEM_SPEED_AND_USER_EXPERIENCE_METRICS && (
          <LineChart
            title={t(DOCUMENT_DASHBOARD.SYSTEM_SPEED_CHART_TITLE.key, DOCUMENT_DASHBOARD.SYSTEM_SPEED_CHART_TITLE.value)}
            description={t(DOCUMENT_DASHBOARD.SYSTEM_SPEED_CHART_DESCRIPTION.key, DOCUMENT_DASHBOARD.SYSTEM_SPEED_CHART_DESCRIPTION.value)}
            height={250}
            width={370}
            colors={['#149486']}
            categories={getLastTwelveMonths()}
            series={[{ name: '', data: chartData.averageSuccess }]}
          />)}
        <LineChart
          title={t(DOCUMENT_DASHBOARD.DOCUMENT_GENERATION_STABILITY_CHART_TITLE.key, DOCUMENT_DASHBOARD.DOCUMENT_GENERATION_STABILITY_CHART_TITLE.value)}
          description={t(DOCUMENT_DASHBOARD.DOCUMENT_GENERATION_STABILITY_CHART_DESCRIPTION.key, DOCUMENT_DASHBOARD.DOCUMENT_GENERATION_STABILITY_CHART_DESCRIPTION.value)}
          height={250}
          width={370}
          colors={['#024AAD']}
          categories={getLastTwelveMonths()}
          series={[{ name: '', data: chartData.averageFailure }]}
        />
        {SHOW_SYSTEM_SPEED_AND_USER_EXPERIENCE_METRICS && (
          <LineChart
            title={t(DOCUMENT_DASHBOARD.USER_EXPIRENCE_METRICS_CHART_TITLE.key, DOCUMENT_DASHBOARD.USER_EXPIRENCE_METRICS_CHART_TITLE.value)}
            description={t(DOCUMENT_DASHBOARD.USER_EXPIRENCE_METRICS_CHART_DESCRIPTION.key, DOCUMENT_DASHBOARD.USER_EXPIRENCE_METRICS_CHART_DESCRIPTION.value)}
            height={250}
            width={370}
            colors={['#DB5538']}
            categories={getLastTwelveMonths()}
            series={[{ name: '', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] }]}
          >
            <LinkText>
              <button className="linkBtn">
                {t(DOCUMENT_DASHBOARD.VIEW_TEMPLATES_TAKING_MORE_TIME_TO_DESIGN_ACTION.key, DOCUMENT_DASHBOARD.VIEW_TEMPLATES_TAKING_MORE_TIME_TO_DESIGN_ACTION.value)}
              </button>
            </LinkText>
          </LineChart>)}
      </ChartsSection>
      <TemplatesList screenWidth={size.chartSectionWidth} templatesView={templatesView}>
        <Modal
          className="templates-list"
          title={t(`${FAILURE_TEMPLATES_MODAL}:${FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL_TITLE.key}`, FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL_TITLE.value)}
          visible={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          footer={null}
          centered
        >
          <ModalData>
            <DashboardErrorReport failureDetails={modalData.failureDetails} />
          </ModalData>
          <ModalHeading>{t(`${FAILURE_TEMPLATES_MODAL}:${FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.FAILURE_DETAILS_HEADING.key}`, FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.FAILURE_DETAILS_HEADING.value)}</ModalHeading>
          <ModalData>{isPayloadValid ? modalData.payload : t(`${FAILURE_TEMPLATES_MODAL}:${FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.NO_DETAILS_AVAILABLE_LABEL.key}`, FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.NO_DETAILS_AVAILABLE_LABEL.value)}</ModalData>
          <ModalHeading>{t(`${FAILURE_TEMPLATES_MODAL}:${FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.RUNTIME_ERROR_MESSAGE.key}`, FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.RUNTIME_ERROR_MESSAGE.value)}</ModalHeading>
          <ModalData>{modalData.runtimeError ? modalData.runtimeError : t(`${FAILURE_TEMPLATES_MODAL}:${FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.NO_DETAILS_AVAILABLE_LABEL.key}`, FAILURE_DOCUMENT_TEMPLATE_DETAILS_MODAL.NO_DETAILS_AVAILABLE_LABEL.value)}</ModalData>
        </Modal>
        <CloseIcon
          onClick={() => {
            setViewType(null);
            setTemplatesView(false);
          }}
        >
          <Icon type="close" />
        </CloseIcon>

        {viewType === 0 ? (
          <FailedTemplates
            columns={columns}
            title={t(`${FAILED_TRANSACTIONS_TABLE}:${FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE_TITLE.key}`, FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE.FAILED_DOCUMENT_TEMPLATE_TRANSACTIONS_TABLE_TITLE.value)}
            activeTab={activeTab}
            selectedTeam={selectedTeam}
            startDate={date.startDate}
            endDate={date.endDate}
            setChartSectionLoader={setChartSectionLoader}
            viewType={viewType}
          />
        ) : (
          <TopTenTemplates
            data={topTenFailureTemplates}
            title={t(DOCUMENT_DASHBOARD.TEMPLATES_PRONE_TO_FAILURE_LIST_TITLE.key, DOCUMENT_DASHBOARD.TEMPLATES_PRONE_TO_FAILURE_LIST_TITLE.value)}
          />
        )}
      </TemplatesList>
    </Dashboard>
  );
};

export default withTranslation(NAMESPACES)(DocumentDashboard);
